import { useTranslation } from 'react-i18next'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import WarningIcon from '@mui/icons-material/Warning'

import colors from '@/assets/scss/_themes-vars.module.scss'

export type ConfirmationDialogProps = {
  /**
   * If `true`, show the loading indicator
   */
  isSubmitting?: boolean
  maxWidth?: number
  /**
   * close dialog handler
   */
  onClose: () => void
  /**
   * delete handler
   */
  onSubmit: () => void
  /**
   * If true, the component is shown.
   * @default false
   */
  open: boolean
  title: string
  message: string
}

export function ConfirmationDialog(props: ConfirmationDialogProps) {
  const { isSubmitting = false, maxWidth, onClose, onSubmit, open = false, title, message } = props
  const { t } = useTranslation()

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: [
          {
            minWidth: '500px',
          },
          !!maxWidth && {
            maxWidth: `${maxWidth}px`,
          },
        ],
      }}
    >
      <DialogContent>
        <Stack alignItems="center" spacing={2}>
          <Avatar sx={{ width: '60px', height: '60px', bgcolor: colors.red100 }}>
            <WarningIcon color="error" fontSize="large" />
          </Avatar>
          <Stack alignItems="center">
            <Typography variant="h6" component="div" sx={{ fontSize: '24px', textAlign: 'center' }}>
              {title}
            </Typography>
            <Typography variant="body1" component="div" sx={{ textAlign: 'center' }}>
              {message}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 16px' }}>
        <Button variant="neutral" onClick={onClose}>
          {t('common:button.cancel.label')}
        </Button>
        <LoadingButton color="error" disableElevation loading={isSubmitting} variant="contained" onClick={onSubmit} data-testid="confirmation-button">
          {t('common:button.confirm.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmationDialog
